import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {CAMPAIGN_APPLICATION_KEYS} from "@/api/campaignApplicationApi";
import WarningIcon from "@/assets/colorable/warning.svg?react";
import AvatarPlaceholder from "@/assets/influencer_profile/avatar-placeholder.png";
import Button from "@/components/Common/Button";
import Modal from "@/components/Common/Modal";
import TextField from "@/components/Common/Primitives/TextField";
import {useIsAdmin} from "@/hooks/useUserHooks";
import {client} from "@/httpClient";
import {Can} from "@/permissions";
import {queryClient} from "@/queryClient";
import {cancelApplicationModalDataAtom} from "@/store/jotaiAtoms";
import {handleErrorWithToast} from "@/utils/errorHandlers";
import {useMutation} from "@tanstack/react-query";
import type {AxiosError} from "axios";
import {useAtom} from "jotai";
import {useMemo, useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";

const CancelApplicationModal: FC = () => {
  const {t} = useTranslation(["campaign_preparation", "common"]);
  const isAdmin = useIsAdmin();
  const [cancelApplicationModalData, setCancelApplicationModalData] = useAtom(cancelApplicationModalDataAtom);
  const application = useMemo(
    () => cancelApplicationModalData?.application!,
    [cancelApplicationModalData?.application],
  );
  const campaign = useMemo(() => cancelApplicationModalData?.campaign!, [cancelApplicationModalData?.campaign]);
  const influencer = useMemo(() => cancelApplicationModalData?.influencer!, [cancelApplicationModalData?.influencer]);
  const canCancel = useMemo(
    () => isAdmin || !campaign.service_is_physical_product || !application.product_sent,
    [campaign.service_is_physical_product, application.product_sent, isAdmin],
  );
  const showInvalidateVoucherWarning = useMemo(
    () => campaign.service_is_voucher && !!application.voucher,
    [campaign.service_is_voucher, application.voucher],
  );
  const [reason, setReason] = useState("");

  const {mutate: cancelApplication, isPending: isCancelingApplication} = useMutation({
    mutationKey: CAMPAIGN_APPLICATION_KEYS.mApplicationCancel(application.id!),
    mutationFn: () => client.post(CAMPAIGN_APPLICATION_KEYS.mApplicationCancel(application.id!)[0], {reason}),
    onSuccess: () => {
      [
        CAMPAIGN_KEYS.qCampaignManagement(campaign.id!),
        CAMPAIGN_KEYS.qCampaignPreparationApplications(campaign.id!, true),
        CAMPAIGN_KEYS.qCampaignPreparationApplications(campaign.id!, false),
        CAMPAIGN_KEYS.qCampaignCancelledApplications(campaign.id!),
      ].map(queryKey => queryClient.invalidateQueries({queryKey}));
      toast.success(t("application_removal_success"));
    },
    onError: (error: AxiosError<{detail: string}>) => handleErrorWithToast(error),
  });

  if (!canCancel) {
    return (
      <Modal
        onClose={() => setCancelApplicationModalData(null)}
        modalTitle={t(
          campaign.ugc ? "influencer_not_removable_modal_title_ugc" : "influencer_not_removable_modal_title",
        )}
        customBottomRow={
          <div className="border-neutral-white-dark flex shrink-0 grow justify-end gap-3 border-t-1 p-5">
            <Button
              onClick={() => setCancelApplicationModalData(null)}
              buttonStyle="White"
              className="min-w-[200px] px-8 py-4">
              {t("cancel")}
            </Button>
          </div>
        }
        backgroundClassName="max-w-[700px]">
        <div className="flex flex-col gap-5">
          <div className="flex flex-col items-center gap-2">
            <span className="text-neutral-black text-sm font-semibold">
              {t(
                campaign.ugc
                  ? "influencer_not_removable_modal_description_ugc"
                  : "influencer_not_removable_modal_description",
              )}
            </span>
            <div className="bg-warning-light-yellow flex flex-col gap-1 self-stretch rounded-md p-3">
              <div className="text-warning-dark-yellow flex items-center gap-2 text-sm font-semibold">
                <WarningIcon className="size-3.5 shrink-0" />
                {t(campaign.ugc ? "influencer_not_removable_modal_title_ugc" : "influencer_not_removable_modal_title")}
              </div>
              <span className="text-neutral-black text-xs">
                {t(
                  campaign.ugc
                    ? "influencer_not_removable_modal_warning_ugc"
                    : "influencer_not_removable_modal_warning",
                )}
              </span>
            </div>
          </div>
          <Can I="create" a="MailToSupport">
            <Button
              onClick={() =>
                (window.location.href = `mailto:info@refluenced.com?subject=${t(campaign.ugc ? "remove_content_creator" : "remove_influencer")} - ${campaign.title}`)
              }
              buttonStyle="White"
              className="h-auto px-3">
              {t("contact_us")}
            </Button>
          </Can>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      onClose={() => setCancelApplicationModalData(null)}
      onConfirm={() => cancelApplication()}
      modalTitle={t(campaign.ugc ? "remove_content_creator" : "remove_influencer")}
      confirmButtonText={t(campaign.ugc ? "remove_content_creator" : "remove_influencer")}
      isConfirmDisabled={!reason || isCancelingApplication}
      isLoading={isCancelingApplication}
      backgroundClassName="max-w-[700px]">
      <div className="flex flex-col items-center gap-5">
        <span className="text-neutral-black text-center text-sm font-semibold">
          {t(campaign.ugc ? "remove_content_creator_from_campaign" : "remove_influencer_from_campaign", {
            campaign_name: campaign.title,
          })}
        </span>
        <div className="flex items-center gap-1.5">
          <img
            src={influencer.picture320 ?? influencer.picture ?? AvatarPlaceholder}
            className="size-7 shrink-0 rounded-full"
          />
          <span className="text-xl font-semibold">{influencer.first_name}</span>
        </div>
        <div className="bg-warning-light-yellow flex items-center gap-2 self-stretch rounded-md p-4">
          <WarningIcon className="text-warning-dark-yellow size-3.5 shrink-0" />
          <div className="flex flex-col gap-1">
            <span className="text-warning-dark-yellow font-semibold">
              {t("influencer_removal_modal_warning_title")}
            </span>
            <span className="text-neutral-black text-xs">
              {t(
                campaign.ugc
                  ? "influencer_removal_modal_warning_description_ugc"
                  : "influencer_removal_modal_warning_description",
              )}
            </span>
          </div>
        </div>
        {showInvalidateVoucherWarning && (
          <div className="bg-warning-light-yellow flex items-center gap-2 self-stretch rounded-md p-4">
            <WarningIcon className="text-warning-dark-yellow size-3.5 shrink-0" />
            <div className="flex flex-col gap-1">
              <span className="text-warning-dark-yellow font-semibold">
                {t(
                  campaign.ugc
                    ? "influencer_removal_modal_voucher_warning_title_ugc"
                    : "influencer_removal_modal_voucher_warning_title",
                )}
              </span>
              <span className="text-neutral-black text-xs">
                {t(
                  campaign.ugc
                    ? "influencer_removal_modal_voucher_warning_description_ugc"
                    : "influencer_removal_modal_voucher_warning_description",
                )}
              </span>
            </div>
          </div>
        )}
        <div className="flex w-full flex-col gap-2 self-start">
          <div className="flex flex-col gap-1">
            <label className="text-neutral-black text-xs">
              {t(campaign.ugc ? "influencer_removal_reason_label_ugc" : "influencer_removal_reason_label")}
            </label>
            <TextField
              text={reason}
              setText={setReason}
              isMultiline
              placeholder={t("influencer_removal_reason_placeholder")}
              className="border-neutral-grey-light-dark min-h-32 resize-none px-3 py-2"
            />
          </div>
          {/* TODO: add "block influencer" checkbox when the backend is ready */}
        </div>
      </div>
    </Modal>
  );
};

export default CancelApplicationModal;
